import { Fragment, useRef, useState, useLayoutEffect, useEffect } from 'react';
import { Button, useBreakpointValue } from '@skin-seoul/design-system';
import type { Review, ReviewImage } from '..';
import { StarIcon } from '../assets/icons/StarIcon';
import { ReviewCardImageCarousel } from './ReviewCardImageCarousel';
import { RepeatPurchaserBadge, VerifiedPurchaserBadge } from './PurchaseBadges';
import { formatRelativeTime } from '@skin-seoul/service/src/utils/review/dateUtils';
import avatar from '../assets/images/avatar.png';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface ReviewCardProps {
  review: Review;
  onImageClick: (image: ReviewImage, index: number) => void;
  onViewMore?: (review: Review) => void;
  /** The card's image carousel settled on a different image (SSD-5573). */
  onImageIndexChange?: (review: Review, toIndex: number) => void;
  isLastInColumn?: boolean;
}

export const ReviewCard = ({
  review,
  onImageClick,
  onViewMore,
  onImageIndexChange,
  isLastInColumn: _isLastInColumn = false,
}: ReviewCardProps) => {
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const hasReviewImages = Boolean(review.images && review.images.length > 0);

  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTextClamped, setIsTextClamped] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const checkTextClamp = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsTextClamped(element.scrollHeight > element.clientHeight);
      }
    };
    const timeoutId = setTimeout(() => {
      checkTextClamp();
    });
    window.addEventListener('resize', checkTextClamp);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTextClamp);
    };
  }, [review.content]);

  return (
    <div className="flex gap-[13px] w-full" data-testid="review-card">
      <div className="p-4 rounded-[8px] border border-neutral-50 w-full grow h-fit tablet:h-[336px]">
        <div className="flex flex-col gap-4 items-start h-full">
          <div className="flex flex-row justify-between items-center w-full gap-2">
            <div className="flex flex-row gap-4 items-center min-w-0">
              <div className="flex flex-row gap-1 items-center shrink-0">
                {/* The card is sized off the H5 mobile ramp at every breakpoint — 20/28 against a 20px star. */}
                <p className="text-heading-h5-mobile font-semibold text-primary-400">{review.rating}</p>
                <StarIcon width={20} height={20} />
              </div>
              {review.verifiedPurchaser && <VerifiedPurchaserBadge />}
            </div>

            <p className="text-neutral-500 text-label-small font-normal shrink-0">
              {formatRelativeTime(review.createdAt)}
            </p>
          </div>
          {isMobile && hasReviewImages && (
            <ReviewCardImageCarousel
              images={review.images || []}
              width="full"
              height="358px"
              onImageClick={onImageClick}
              onIndexChange={(toIndex) => onImageIndexChange?.(review, toIndex)}
            />
          )}
          <div className="flex flex-col gap-4 tablet:gap-3 items-start w-full h-full justify-between">
            <div className="flex flex-col items-start gap-4 tablet:gap-2">
              <p className="text-paragraph-large font-semibold text-neutral-800">{review.title}</p>
              <p ref={textRef} className="text-paragraph-small font-normal text-neutral-500 line-clamp-3">
                {review.content}
              </p>
              {isTextClamped && (
                <div>
                  <Button
                    className="max-w-[fit-content] max-h-[fit-content] text-primary-400 text-label-small px-0"
                    variant="plain"
                    onClick={() => onViewMore?.(review)}
                  >
                    View more
                  </Button>
                </div>
              )}
            </div>
            <div className="flex flex-row rounded-[8px] p-2 bg-neutral-50 w-full gap-2">
              <img src={avatar.src} alt="avatar" className="w-8 h-8 rounded-full shrink-0" />
              <div className="flex flex-col items-start gap-0.5 w-full">
                {/* The badge reads as part of the name, so it sits against it. Pushed to the far
                    edge it lost that link on a desktop-width card. */}
                <div className="flex flex-row items-center gap-2 w-full min-w-0">
                  <p className="text-label-small font-medium text-neutral-800 truncate">{review.author.name}</p>
                  {review.repeatPurchaser && <RepeatPurchaserBadge />}
                </div>
                <div className="flex flex-row gap-1 flex-wrap">
                  {[...review.skinType, ...review.skinConcern].map((type, index, array) => (
                    <Fragment key={index}>
                      <p className="text-label-xsmall font-normal text-neutral-700">{type}</p>
                      {index < array.length - 1 && <div className="w-0.5 h-0.5 bg-neutral-100 rounded-full" />}
                    </Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isMobile ? (
        hasReviewImages ? (
          <ReviewCardImageCarousel
            images={review.images || []}
            width="279px"
            height="336px"
            onImageClick={onImageClick}
            onIndexChange={(toIndex) => onImageIndexChange?.(review, toIndex)}
          />
        ) : (
          <div className="w-[279px] shrink-0" />
        )
      ) : null}
    </div>
  );
};

export default ReviewCard;
